import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ChevronDown, Gift, Cake, ExternalLink, FileText, Download, MessageCircle } from "lucide-react";
import { palisisShop, PALISIS_URL, FARES, FARE_INFO, GIFT_VOUCHERS, GIFT_VOUCHER_URL, BIRTHDAY_VOUCHER_URL } from "@/lib/site";
import { sharePdfOnWhatsApp } from "@/lib/share";

const PRICE_FLYER = "/ticket-prices-2026-2027.pdf";

function InfoPanel({ id, open }) {
  const info = FARE_INFO[id];
  return (
    <AnimatePresence initial={false}>
      {open && info && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
          data-testid={`pricing-info-panel-${id}`}
        >
          <ul className="mt-3 space-y-2 border-t border-[#fff7e8]/15 pt-3">
            {info.bullets.map((b, i) => (
              <li key={i} className="flex gap-2 text-xs leading-relaxed text-[#fff7e8]/70">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#f8b818]" />
                {b}
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function InfoToggle({ id, open, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`mt-4 inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest transition-colors ${open ? "text-[#f8b818]" : "text-[#f8b818]/80 hover:text-[#f8b818]"}`}
      data-testid={`pricing-info-btn-${id}`}
      aria-expanded={open}
    >
      <Info size={13} /> {open ? "Less info" : "More info"}
      <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

function CardImage({ id, alt }) {
  const info = FARE_INFO[id];
  if (!info) return null;
  return (
    <div className="relative -mx-6 -mt-6 mb-4 h-28 overflow-hidden" data-testid={`pricing-card-img-${id}`}>
      <img src={info.img} alt={alt} loading="lazy" className="h-full w-full object-cover object-[center_30%]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#241c0d]/35 to-transparent" />
    </div>
  );
}

export default function Pricing() {
  const [openInfo, setOpenInfo] = useState(null);
  const tiered = FARES.filter((f) => ["single", "three", "six", "nappy", "family"].includes(f.id));
  const passes = FARES.filter((f) => ["day", "workday", "fast"].includes(f.id));
  const toggle = (id) => setOpenInfo((cur) => (cur === id ? null : id));

  return (
    <section id="pricing" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="pricing-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <h2 className="max-w-2xl text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>SIMPLE. FAIR. FAST.</h2>
        </div>
        <span className="btn-red" data-testid="pricing-save10-badge">
          Save 10% when you buy online!
        </span>
      </motion.div>

      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {tiered.map((f, i) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: i * 0.06 }}
            className={`group relative flex flex-col overflow-hidden rounded-3xl border-2 border-[#f8b818]/35 bg-[#14120c] p-6 shadow-[0_14px_35px_rgba(36,28,13,0.22)] transition-all hover:-translate-y-1 hover:border-[#f8b818] ${["nappy", "family"].includes(f.id) ? "lg:col-span-3" : "lg:col-span-2"}`}
            data-testid={`pricing-card-${f.id}`}
          >
            {f.id === "family" && (
              <span
                className="absolute right-4 top-4 z-10 rounded-full bg-[#f8b818] px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#14120c] shadow-[0_6px_18px_rgba(248,184,24,0.5)]"
                data-testid="pricing-best-seller-badge"
              >
                Bestseller
              </span>
            )}
            <CardImage id={f.id} alt={`${f.name} — downhill toboggan product picture`} />
            <p className="text-sm font-extrabold uppercase tracking-widest text-[#f8b818]">{f.rides}</p>
            <h3 className="mt-2 text-2xl leading-tight text-[#fff7e8]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>{f.name}</h3>
            {f.childPrice ? (
              <div className="mt-4 flex items-start gap-5" data-testid={`pricing-prices-${f.id}`}>
                <div className="text-left">
                  <span className="block font-display text-4xl leading-none text-[#f8b818]" data-testid={`pricing-child-price-${f.id}`}>{f.childPrice}</span>
                  <span className="mt-1 block text-[10px] font-extrabold uppercase tracking-widest text-[#fff7e8]/60">Child 3–14</span>
                </div>
                <span className="h-9 w-px self-center bg-[#fff7e8]/20" />
                <div className="text-left">
                  <span className="block font-display text-4xl leading-none text-[#f8b818]" data-testid={`pricing-adult-price-${f.id}`}>{f.adultPrice}</span>
                  <span className="mt-1 block text-[10px] font-extrabold uppercase tracking-widest text-[#fff7e8]/60">Adult 15+</span>
                </div>
              </div>
            ) : (
              <p className="font-display mt-4 text-4xl leading-none text-[#f8b818]">{f.price}</p>
            )}
            <p className="mt-3 text-sm text-[#fff7e8]/70">{f.note}</p>
            <div className="mt-auto">
              <InfoToggle id={f.id} open={openInfo === f.id} onClick={() => toggle(f.id)} />
              <InfoPanel id={f.id} open={openInfo === f.id} />
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={f.shop ? palisisShop(f.shop, f.shopBase) : PALISIS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold !px-4 !py-2.5 !text-xs"
                  data-testid={`pricing-shop-${f.id}`}
                >
                  <ExternalLink size={12} /> Buy Ticket
                </a>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {passes.map((f, i) => (
          <motion.div
            key={f.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.55, delay: 0.24 + i * 0.06 }}
            className="group flex flex-col overflow-hidden rounded-3xl border-2 border-[#f8b818]/35 bg-[#14120c] p-6 shadow-[0_14px_35px_rgba(36,28,13,0.22)] transition-all hover:-translate-y-1 hover:border-[#f8b818]"
            data-testid={`pricing-card-${f.id}`}
          >
            <CardImage id={f.id} alt={`${f.name} — downhill toboggan product picture`} />
            <p className="text-sm font-extrabold uppercase tracking-widest text-[#f8b818]">{f.rides}</p>
            <h3 className="mt-2 text-2xl leading-tight text-[#fff7e8]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>{f.name}</h3>
            <p className="font-display mt-4 text-4xl text-[#f8b818]" data-testid={`pricing-pass-price-${f.id}`}>{f.price}</p>
            <p className="mt-3 text-sm text-[#fff7e8]/70">{f.note}</p>
            <div className="mt-auto">
              <InfoToggle id={f.id} open={openInfo === f.id} onClick={() => toggle(f.id)} />
              <InfoPanel id={f.id} open={openInfo === f.id} />
              <div className="mt-4 flex flex-wrap gap-2">
                {f.shop ? (
                  <a
                    href={palisisShop(f.shop, f.shopBase)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold !px-5 !py-2.5 !text-xs"
                    data-testid={`pricing-shop-${f.id}`}
                  >
                    <ExternalLink size={12} /> Buy Ticket
                  </a>
                ) : null}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="mt-4"
        data-testid="price-flyer-strip"
      >
        <div className="card-dark flex flex-wrap items-center gap-8 p-8">
          <a
            href={PRICE_FLYER}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex h-56 w-full max-w-[280px] items-center justify-center rounded-2xl bg-[#faf5ea] p-2"
            data-testid="price-flyer-thumb"
          >
            <img
              src="/flyer/flyer-front.png"
              alt="Cool Runnings ticket prices 2026 / 2027 flyer"
              className="h-full w-full rounded-xl object-contain transition-transform group-hover:scale-[1.03]"
              loading="lazy"
              data-testid="price-flyer-image"
            />
          </a>
          <div className="min-w-[240px] flex-1">
            <p className="eyebrow">Take the prices with you</p>
            <h3 className="mt-2 flex items-center gap-3 text-3xl leading-tight text-[#c87700]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
              <FileText size={26} /> PRICE FLYER
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-[#6b5a3a]">
              The full 2026 / 2027 price list as a PDF — download it for the fridge door or send it straight to the
              family WhatsApp group.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={PRICE_FLYER}
                download="Cool-Runnings-Ticket-Prices-2026-2027.pdf"
                className="btn-gold !px-5 !py-2.5 !text-xs"
                data-testid="price-flyer-download-btn"
              >
                <Download size={14} /> Download PDF
              </a>
              <button
                type="button"
                onClick={() => sharePdfOnWhatsApp("Ticket Prices 2026 / 2027", PRICE_FLYER)}
                aria-label="Share the price flyer PDF on WhatsApp"
                className="flex items-center gap-2 rounded-full bg-[#25d366] px-5 py-2.5 text-xs font-extrabold text-white transition-transform hover:scale-105"
                data-testid="price-flyer-whatsapp-btn"
              >
                <MessageCircle size={14} /> Share on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="mt-14"
        data-testid="gift-vouchers-strip"
      >
        <div className="card-dark flex flex-wrap items-center gap-8 p-8">
          <span className="flex h-56 w-full max-w-[280px] items-center justify-center rounded-2xl bg-[#faf5ea] p-2">
            <img
              src="/products/gift-voucher-art.jpg"
              alt="Cool Runnings gift voucher artwork — give a downhill adventure"
              className="h-full w-full rounded-xl object-contain"
              loading="lazy"
              data-testid="gift-voucher-image"
            />
          </span>
          <div className="min-w-[240px] flex-1">
            <p className="eyebrow">Give the track</p>
            <h3 className="mt-2 flex items-center gap-3 text-3xl leading-tight text-[#c87700]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
              <Gift size={26} /> GIFT VOUCHERS
            </h3>
            <div className="mt-5 flex flex-wrap gap-3">
              {GIFT_VOUCHERS.map((v) => (
                <a
                  key={v}
                  href={GIFT_VOUCHER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline !px-5 !py-2.5 !text-xs"
                  data-testid={`gift-voucher-btn-${v.replace("R", "")}`}
                >
                  {v} Voucher →
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="card-dark mt-4 flex flex-wrap items-center gap-8 p-8" data-testid="birthday-voucher-strip">
          <span className="flex h-56 w-full max-w-[280px] items-center justify-center rounded-2xl bg-[#faf5ea] p-2">
            <img
              src="/products/birthday-voucher-art.jpg"
              alt="Cool Runnings birthday voucher artwork — happy birthday from the toboggan park"
              className="h-full w-full rounded-xl object-contain"
              loading="lazy"
              data-testid="birthday-voucher-image"
            />
          </span>
          <div className="min-w-[240px] flex-1">
            <p className="eyebrow">Make their day</p>
            <h3 className="mt-2 flex items-center gap-3 text-3xl leading-tight text-[#c87700]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
              <Cake size={26} /> BIRTHDAY VOUCHER
            </h3>
            <div className="mt-5 flex flex-wrap gap-3">
              {GIFT_VOUCHERS.map((v) => (
                <a
                  key={v}
                  href={BIRTHDAY_VOUCHER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline !px-5 !py-2.5 !text-xs"
                  data-testid={`birthday-voucher-btn-${v.replace("R", "")}`}
                >
                  {v} Voucher →
                </a>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
